// ─────────────────────────────────────────────────────────────────────
// WHAT THIS COURT WAS LAST SEEN DOING — the pad's own, device-local note (#479).
//
// A wall-mounted tablet reboots for ordinary reasons: a power blip, an Android
// update, somebody unplugging it. #490 fixed the worst of doing that during an
// outage — the pad no longer sits on a dark splash for ever, it says it cannot
// reach Starpoint. But it says only that, and the hall is standing in front of
// a court whose match it can no longer see.
//
// So the pad keeps a one-line note of what was on the court, and the offline
// screen shows it. That is the WHOLE feature, and the narrowness is the point.
//
// ── IT IS A NOTE, NOT A CACHED VIEW ──────────────────────────────────
//
// #479 names the hazard this has to avoid:
//
//   > never invent fresh server state while offline · do not allow stale
//   > cached authority to create duplicate/different matches
//
// A stored VIEW would be re-renderable, and a re-rendered court comes with
// buttons — at which point a result could be recorded against a match the
// server may already have finalised. `padResultRequestId` does not save that
// case: it makes a replay of the SAME result idempotent, and a stale view
// naming a DIFFERENT match is not a replay.
//
// This stores strings for a person to read: names, a score, a time. It is not
// the shape the renderers take and cannot be passed to them, which is a
// property worth keeping rather than a limitation to work around later. If a
// cached court view is ever wanted, it is a separate decision and a separate
// issue — not an extension of this file.
//
// ── WHY `window.localStorage` DIRECTLY ───────────────────────────────
//
// Same reason as the device token (see banepadDeviceSession.ts): the shared
// `Storage` abstraction is backed by the club's SERVER store, which is exactly
// what is unreachable when this note is needed. A note that requires the
// network to read is not a note. It is per-tablet, like the token beside it.
//
// ── WHAT IS ON THE DEVICE, AND FOR HOW LONG ──────────────────────────
//
// Player NAMES, a score and a time — no ids, no ratings, no contact details,
// nothing about anybody not on this court. The same names the tablet has been
// displaying in a public hall all evening, and strictly less than the device
// token beside them already grants. It is cleared when the pad is reset, and it
// expires on its own.
// ─────────────────────────────────────────────────────────────────────

/** localStorage key for this tablet's last-known court note. */
export const PAD_LAST_KNOWN_STORAGE_KEY = 'banepad_last_known_v1';

/**
 * How old a note may be and still be shown.
 *
 * Two hours covers a long outage plus a slow reboot, and stops short of
 * yesterday: a tablet powered off overnight must not greet the morning with
 * last night's match. The screen always prints the time the note was taken, so
 * within the window staleness is visible rather than implied — the bound is
 * there to stop the note becoming archaeology, not to make it trustworthy.
 */
export const PAD_LAST_KNOWN_MAX_AGE_MS = 2 * 60 * 60 * 1000;

/** A human-readable note about what a court was last seen doing. */
export interface PadLastKnown {
  /**
   * Which court the note is about.
   *
   * Checked on read, because a tablet can be re-paired to a different court and
   * showing the previous court's match on the new one would be a worse failure
   * than showing nothing at all.
   */
  court: string;
  /** Epoch ms when this tablet stored the note. */
  savedAt: number;
  /** The session's display name, when it had one. */
  title: string | null;
  /** Whether the match was being played, as opposed to merely next up. */
  live: boolean;
  /** Who was on court, by name only. */
  players: string[];
  /** The score as last seen, already rendered — `'4–3'`, or null. */
  score: string | null;
}

/**
 * Derive the note from a `/pad/state` view.
 *
 * Reads only the view's TOP-LEVEL fields, which is where the projection already
 * answers "what is on this court": `players` names who is on it, `live` says
 * whether it is being played, `title` names the session. Nothing here reaches
 * into `screen` or reconstructs anything the server did not say.
 *
 * Returns null when there is nothing worth remembering — an idle court's note
 * would be «this court was idle», which the offline screen already implies.
 */
export function padLastKnownFrom(view: unknown, at: number = Date.now()): PadLastKnown | null {
  if (!view || typeof view !== 'object') return null;
  const v = view as Record<string, unknown>;
  if (v.match == null) return null;

  const court = v.court == null ? '' : String(v.court);
  if (!court) return null;

  const players = Array.isArray(v.players)
    ? v.players
        .map((p) =>
          p && typeof p === 'object' ? String((p as { name?: unknown }).name ?? '') : '',
        )
        .filter((n) => n.trim() !== '')
    : [];

  const title = typeof v.title === 'string' && v.title.trim() !== '' ? v.title : null;

  return {
    court,
    savedAt: at,
    title,
    live: v.live === true,
    players,
    score: renderLiveScore((v.match as Record<string, unknown>).liveScore),
  };
}

/**
 * The in-progress score, as the pad itself reads it.
 *
 * `liveScore` is `{ score1, score2 }` with either side possibly null — it is
 * written continuously as an operator types, so a half-entered score is the
 * normal state rather than an error. Half of one says nothing to a reader, so
 * it is treated as no score at all.
 */
function renderLiveScore(liveScore: unknown): string | null {
  if (!liveScore || typeof liveScore !== 'object') return null;
  const { score1, score2 } = liveScore as { score1?: unknown; score2?: unknown };
  if (typeof score1 !== 'number' || typeof score2 !== 'number') return null;
  return `${score1}–${score2}`;
}

/** Stores the note for this tablet, replacing any previous one. */
export function writePadLastKnown(note: PadLastKnown): void {
  try {
    window.localStorage.setItem(PAD_LAST_KNOWN_STORAGE_KEY, JSON.stringify(note));
  } catch {
    /* storage unavailable — a kiosk without it simply has no note */
  }
}

/**
 * The note for `court`, or null when there is none worth showing.
 *
 * Refuses another court's note and an expired one. Anything unparseable or
 * misshapen is also null: this is read on the one screen that exists because
 * something is already wrong, so it fails quiet rather than throwing into it.
 */
export function readPadLastKnown(
  court: string,
  now: number = Date.now(),
  maxAgeMs: number = PAD_LAST_KNOWN_MAX_AGE_MS,
): PadLastKnown | null {
  try {
    const raw = window.localStorage.getItem(PAD_LAST_KNOWN_STORAGE_KEY);
    if (!raw) return null;
    const note = JSON.parse(raw) as PadLastKnown;
    if (!note || typeof note !== 'object') return null;
    if (String(note.court) !== String(court)) return null;
    if (typeof note.savedAt !== 'number' || !Number.isFinite(note.savedAt)) return null;
    if (now - note.savedAt > maxAgeMs) return null;
    if (note.savedAt > now + 60_000) return null; // clock moved backwards; not evidence
    if (!Array.isArray(note.players)) return null;
    return note;
  } catch {
    return null;
  }
}

/** Forgets the note. Called wherever this tablet's pairing is cleared. */
export function clearPadLastKnown(): void {
  try {
    window.localStorage.removeItem(PAD_LAST_KNOWN_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}
